import { CheckCircle, WarningCircle, XCircle, Prohibit, SealCheck, SealWarning, ArrowClockwise, ArrowRight } from "@phosphor-icons/react";
import { ModuleLoadState } from "@/hooks/useModuleData";

const STATUS_META = {
  complete: { label: "Complete", tone: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle },
  partial: { label: "Partial", tone: "bg-amber-50 text-amber-700 border-amber-300", Icon: WarningCircle },
  missing: { label: "Missing", tone: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
  conflicting: { label: "Conflicting", tone: "bg-purple-50 text-purple-700 border-purple-300", Icon: Prohibit },
  unverified: { label: "Unverified", tone: "bg-orange-50 text-orange-700 border-orange-300", Icon: SealWarning },
};

export const ReportInputReadiness = ({ data, m, onProceed }) => {
  if (!data) return <ModuleLoadState {...m} />;
  const counts = data.counts || {};
  return (
    <div data-testid="input-readiness-panel" className="space-y-4 max-w-4xl mx-auto">
      <div className={`border px-5 py-4 ${data.consultant_grade ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
        <div className="flex items-center gap-2">
          {data.consultant_grade ? <SealCheck size={18} className="text-emerald-700" /> : <SealWarning size={18} className="text-slate-500" />}
          <p data-testid="grade-label" className="font-heading font-bold text-sm">{data.grade_label}</p>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Release status: <span className="font-semibold">{data.release_status}</span> · Release gate {data.release_can_release ? "clean" : "has open blockers"}
        </p>
      </div>

      {data.final_blocked && (
        <div data-testid="final-blocked-banner" className="bg-red-50 border border-red-200 px-5 py-4">
          <p className="font-heading font-bold text-sm text-red-700">Final approval and external final export are blocked</p>
          <p className="text-xs text-red-600 mt-1">
            Critical inputs are unverified, missing or conflicting: {data.blocking_areas.map((b) => `${b.label} (${b.status})`).join(" · ")}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {Object.entries(STATUS_META).map(([k, meta]) => (
          <div key={k} data-testid={`readiness-count-${k}`} className={`border px-3 py-2 ${meta.tone}`}>
            <p className="text-[9px] uppercase tracking-widest font-bold">{meta.label}</p>
            <p className="font-heading font-extrabold text-xl">{counts[k] ?? 0}</p>
          </div>
        ))}
      </div>

      <section className="bg-white border border-slate-200 divide-y divide-slate-100">
        <div className="px-5 py-3 flex items-center justify-between">
          <p className="overline-label text-slate-400">Twelve report input areas</p>
          <button data-testid="readiness-refresh" onClick={m.retry}
            className="text-[11px] font-semibold text-slate-600 border border-slate-300 px-2.5 py-1 hover:bg-slate-50 flex items-center gap-1">
            <ArrowClockwise size={11} /> Re-assess
          </button>
        </div>
        {data.areas.map((a) => {
          const meta = STATUS_META[a.status] || STATUS_META.missing;
          return (
            <div key={a.id} data-testid={`readiness-area-${a.id}`} className="px-5 py-3 flex items-start gap-3">
              <meta.Icon size={16} className={`mt-0.5 shrink-0 ${meta.tone.split(" ")[1]}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-xs font-semibold text-slate-800">{a.label}</p>
                  <span data-testid={`readiness-status-${a.id}`} className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${meta.tone}`}>{meta.label}</span>
                  {a.critical && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border border-slate-300 text-slate-500">Critical</span>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{a.summary}</p>
                {a.status !== "complete" && a.remediation && (
                  <p className="text-[11px] text-slate-400 mt-0.5">→ {a.remediation}</p>
                )}
              </div>
            </div>
          );
        })}
      </section>

      {data.draft_limitations.length > 0 && (
        <section data-testid="draft-limitations" className="bg-amber-50/60 border border-amber-200 px-5 py-4">
          <p className="overline-label text-amber-700">Draft limitations ({data.draft_limitations.length})</p>
          <p className="text-[11px] text-amber-800 mt-1 mb-2">
            A draft report is available now. Every external draft export will carry this limitations list on its first page.
          </p>
          <ul className="space-y-1">
            {data.draft_limitations.map((l, i) => (
              <li key={i} className="text-[11px] text-amber-900">• {l}</li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex justify-end">
        <button data-testid="proceed-to-draft-btn" onClick={onProceed}
          className="bg-slate-900 text-white text-xs font-semibold px-5 py-2.5 hover:bg-slate-800 flex items-center gap-1.5">
          Continue to {data.final_blocked ? "draft report (with limitations)" : "report"} <ArrowRight size={13} />
        </button>
      </div>
    </div>
  );
};
